import { motion } from "framer-motion";
import { CountUp } from "./CountUp";
import { money } from "@/lib/utils";
import { TrendingUp, Zap, ShieldAlert, Wallet, Award, Flame, Info } from "lucide-react";
import AdvancedStrip from "./AdvancedStrip";
import { platformAdvancedRows, sellThroughAdvanced } from "@/lib/advanced";

const stagger = { animate: { transition: { staggerChildren: 0.08 } } };
const item = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.2, 0.9, 0.2, 1] } },
};

function GlassCard({ children, className = "", testid }) {
  return (
    <motion.div variants={item} data-testid={testid} className={`glass p-4 sm:p-6 ${className}`}>
      {children}
    </motion.div>
  );
}

function StatLabel({ icon: Icon, children, tint = "text-white/60" }) {
  return (
    <div className={`flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-[11px] uppercase tracking-[0.18em] sm:tracking-[0.2em] font-mono-num ${tint}`}>
      <Icon size={12} /> {children}
    </div>
  );
}

export default function Report({ report, marketAnalytics, onAutoAdjust, plan = "free", onUpgrade }) {
  const { prices, sell_through, platforms, condition, profit } = report;
  const unlockedAdvanced = plan === "reseller" || plan === "pro";
  const platformAdv = platformAdvancedRows(report);
  const stAdv = sellThroughAdvanced(report);

  // price spectrum positions
  const min = prices.quick_sale;
  const max = prices.high_end;
  const pct = (v) => Math.max(0, Math.min(100, ((v - min) / (max - min)) * 100));
  const recPct = pct(prices.recommended);

  // ROI chip: recommended vs. live market avg (from eBay comps)
  const avg = marketAnalytics?.avg_sold_price;
  const rec = prices.recommended;
  let roiChip = null;
  if (typeof avg === "number" && avg > 0 && typeof rec === "number") {
    const delta = Math.round(rec - avg);
    if (delta < 0) {
      roiChip = {
        kind: "under",
        text: `Underpriced by ${money(Math.abs(delta))} vs. market avg`,
        icon: Flame,
      };
    } else if (delta > 0) {
      roiChip = {
        kind: "over",
        text: `Priced ${money(delta)} above market avg`,
        icon: Info,
      };
    } else {
      roiChip = { kind: "match", text: "Priced exactly at market avg", icon: TrendingUp };
    }
  }

  const canAutoAdjust =
    typeof onAutoAdjust === "function" &&
    roiChip &&
    (roiChip.kind === "under" || roiChip.kind === "over");

  return (
    <motion.div
      variants={stagger}
      initial="initial"
      animate="animate"
      className="space-y-5"
      data-testid="report-container"
    >
      {/* Mobile sticky summary — visible only on <md while scrolling */}
      <div
        className="md:hidden sticky-result-summary flex items-center justify-between gap-3"
        data-testid="mobile-sticky-summary"
      >
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[#64748B] font-medium">List at</div>
          <div className="font-mono-num font-bold text-lg text-[#047857] truncate">
            {money(prices.recommended)}
          </div>
        </div>
        <div className="text-right min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[#64748B] font-medium">Net take-home</div>
          <div className="font-mono-num font-bold text-lg text-[#0F172A] truncate">
            {money(profit.net)}
          </div>
        </div>
      </div>

      {/* Title */}
      <GlassCard testid="report-title-card">
        <StatLabel icon={Award} tint="text-[#10B981]">Identified Item</StatLabel>
        <h2 data-testid="item-title" className="font-display text-xl sm:text-2xl md:text-4xl font-bold tracking-tight mt-2 text-white leading-tight">
          {report.item_title}
        </h2>
        <div className="mt-3 flex flex-wrap gap-1.5 sm:gap-2">
          {[report.brand, report.era, report.material].filter(Boolean).map((t) => (
            <span key={t} className="text-[10px] sm:text-xs px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-white/[0.05] border border-white/10 text-white/70 font-mono-num">
              {t}
            </span>
          ))}
        </div>
      </GlassCard>

      {/* Price spectrum */}
      <GlassCard testid="price-spectrum-card">
        <div className="flex items-center justify-between mb-5 sm:mb-6 gap-3">
          <StatLabel icon={TrendingUp} tint="text-[#F59E0B]">Price Spectrum</StatLabel>
          <div className="text-right">
            <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-white/40">Recommended</div>
            <div className="font-mono-num font-bold text-xl sm:text-2xl text-[#10B981]" data-testid="recommended-price">
              <CountUp value={prices.recommended} prefix="$" decimals={0} />
            </div>
            <div
              className="text-[9px] sm:text-[10px] text-white/40 mt-0.5 tracking-wide"
              data-testid="price-disclaimer"
              title="Actual selling price depends on your listing, timing and demand."
            >
              Estimate, not a guarantee
            </div>
          </div>
        </div>

        {roiChip && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-5 flex flex-wrap items-center gap-2"
          >
            <span
              data-testid={`roi-chip-${roiChip.kind}`}
              className={
                "inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-semibold border " +
                (roiChip.kind === "under"
                  ? "bg-[#10B981]/10 border-[#10B981]/40 text-[#10B981]"
                  : roiChip.kind === "over"
                  ? "bg-[#F59E0B]/10 border-[#F59E0B]/40 text-[#F59E0B]"
                  : "bg-white/[0.06] border-white/20 text-white/80")
              }
            >
              {roiChip.kind === "under" && <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" aria-hidden />}
              <roiChip.icon size={13} />
              <span>{roiChip.text}</span>
            </span>

            {canAutoAdjust && (
              <button
                type="button"
                data-testid="auto-adjust-btn"
                onClick={() => onAutoAdjust(avg)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-semibold text-white bg-[#0F172A] hover:bg-[#1E293B] transition-colors"
                aria-label="Auto-adjust recommended price to market average"
              >
                <span>Match market avg</span>
              </button>
            )}
          </motion.div>
        )}

        <div className="relative pt-8 pb-2">
          {/* recommended callout — keep centered but clamp so it never overflows on mobile */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="absolute -top-1 -translate-x-1/2"
            style={{ left: `clamp(14%, ${recPct}%, 86%)` }}
          >
            <div className="px-2 sm:px-2.5 py-1 rounded-md bg-[#0F172A] text-white text-[10px] sm:text-[11px] font-bold whitespace-nowrap shadow-lg">
              List @ {money(prices.recommended)}
            </div>
            <div className="mx-auto w-px h-2 bg-[#0F172A]/40" />
          </motion.div>

          {/* bar */}
          <div className="relative h-3 rounded-full bg-white/[0.06] overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#F59E0B] via-[#6366F1] to-[#10B981] rounded-full"
            />
          </div>

          {/* ticks */}
          <div className="grid grid-cols-3 mt-5 gap-2 sm:gap-3">
            {[
              { l: "Quick Sale", v: prices.quick_sale, note: "Sells < 48 hrs", tint: "text-[#F59E0B]" },
              { l: "Fair Market", v: prices.fair_market, note: "Standard pace", tint: "text-white" },
              { l: "High-End", v: prices.high_end, note: "Max profit", tint: "text-[#10B981]" },
            ].map((t) => (
              <div key={t.l} className="text-center">
                <div className={`font-mono-num font-bold text-base sm:text-xl ${t.tint}`}>
                  <CountUp value={t.v} prefix="$" decimals={0} />
                </div>
                <div className="text-[11px] sm:text-xs text-white/70 font-medium">{t.l}</div>
                <div className="hidden sm:block text-[10px] text-white/40 mt-0.5">{t.note}</div>
              </div>
            ))}
          </div>
        </div>
      </GlassCard>

      {/* 2x2 stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
        {/* Sell-through */}
        <GlassCard testid="sellthrough-card">
          <StatLabel icon={Zap} tint="text-[#10B981]">Sell-Through Rate</StatLabel>
          <div className="mt-3 flex items-end justify-between gap-3">
            <div>
              <div className="font-mono-num text-4xl sm:text-5xl font-bold text-white">
                <CountUp value={sell_through.rate} decimals={0} />%
              </div>
              <div className="text-[#047857] text-xs sm:text-sm font-medium mt-1">{sell_through.label}</div>
            </div>
            <span className="px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full bg-[#10B981]/10 border border-[#10B981]/30 text-[#10B981] text-[11px] sm:text-xs font-mono-num whitespace-nowrap">
              Est. {sell_through.days_min}–{sell_through.days_max} days
            </span>
          </div>
          <div className="mt-4 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${sell_through.rate}%` }}
              transition={{ duration: 1.2, delay: 0.2 }}
              className="h-full bg-gradient-to-r from-[#10B981] to-[#6366F1]"
            />
          </div>
          {stAdv && (
            <AdvancedStrip
              testid="sellthrough-advanced"
              locked={!unlockedAdvanced}
              ctaTier="reseller"
              onUpgrade={onUpgrade}
              heading="Advanced · demand depth"
              ctaLabel="See the real 30-day comp count and median days-to-sell for your item"
            >
              <div className="grid grid-cols-3 gap-3 text-white/85">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40">~ sold last 30d</div>
                  <div className="mt-0.5 font-mono-num text-lg sm:text-xl font-bold">~{stAdv.est_sold_last_30d}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Median days</div>
                  <div className="mt-0.5 font-mono-num text-lg sm:text-xl font-bold">{stAdv.median_days_to_sell ?? "—"}d</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Demand bucket</div>
                  <div className="mt-0.5 text-xs sm:text-sm font-medium">{stAdv.demand_bucket || "—"}</div>
                </div>
              </div>
            </AdvancedStrip>
          )}
        </GlassCard>

        {/* Platform match */}
        <GlassCard testid="platform-match-card">
          <StatLabel icon={Award} tint="text-[#6366F1]">Platform Match Matrix</StatLabel>
          <div className="mt-3 space-y-2.5">
            {platforms.map((p, i) => (
              <div key={p.name} className="flex items-center gap-3">
                <div className={`w-6 h-6 rounded-md flex items-center justify-center text-[11px] font-bold font-mono-num ${
                  i === 0 ? "bg-[#10B981] text-black" : i === 1 ? "bg-[#6366F1] text-white" : "bg-white/10 text-white/70"
                }`}>#{i + 1}</div>
                <div className="flex-1">
                  <div className="flex justify-between items-baseline">
                    <span className="font-semibold text-white text-sm">{p.name}</span>
                    <span className="font-mono-num text-sm font-bold text-white">{p.match}%</span>
                  </div>
                  <div className="text-[11px] text-white/50">{p.note}</div>
                  <div className="mt-1 h-1 rounded-full bg-white/[0.06] overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${p.match}%` }}
                      transition={{ duration: 1, delay: 0.1 * i + 0.2 }}
                      className={`h-full ${
                        i === 0 ? "bg-[#10B981]" : i === 1 ? "bg-[#6366F1]" : "bg-white/40"
                      }`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          {platformAdv.length > 0 && (
            <AdvancedStrip
              testid="platform-advanced"
              locked={!unlockedAdvanced}
              ctaTier="reseller"
              onUpgrade={onUpgrade}
              heading="Advanced · net margin & velocity"
              ctaLabel="See after-fee net and days-to-sell per platform for this item"
            >
              <div className="overflow-x-auto -mx-1 px-1">
                <table className="w-full min-w-[320px] text-xs sm:text-sm text-white/85">
                  <thead className="text-[10px] uppercase tracking-widest text-white/40">
                    <tr>
                      <th className="text-left py-1 pr-2">Platform</th>
                      <th className="text-right py-1 pr-2">Est. net</th>
                      <th className="text-right py-1 pr-2">Fees</th>
                      <th className="text-right py-1">~ days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {platformAdv.map((r) => (
                      <tr key={r.name} className="border-t border-white/[0.05]">
                        <td className="py-1.5 pr-2 font-medium">{r.name}</td>
                        <td className="py-1.5 pr-2 text-right font-mono-num font-semibold text-[#10B981]">${r.net_usd.toFixed(2)}</td>
                        <td className="py-1.5 pr-2 text-right font-mono-num text-white/60">-${r.fee_usd.toFixed(2)}</td>
                        <td className="py-1.5 text-right font-mono-num">{r.velocity_days != null ? `${r.velocity_days}d` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </AdvancedStrip>
          )}
        </GlassCard>

        {/* Condition */}
        <GlassCard testid="condition-card">
          <StatLabel icon={ShieldAlert} tint="text-[#F59E0B]">Condition Detector</StatLabel>
          <div className="mt-3 flex items-baseline gap-2">
            <div className="font-mono-num font-bold text-4xl sm:text-5xl text-white">
              <CountUp value={condition.score} decimals={1} />
            </div>
            <div className="text-white/40 text-base sm:text-lg font-mono-num">/ 10</div>
          </div>
          <div className="text-xs sm:text-sm text-[#F59E0B] font-medium mb-2 sm:mb-3">{condition.label}</div>
          <p className="text-[11px] sm:text-xs text-white/60 leading-relaxed">{condition.flaw_note}</p>
        </GlassCard>

        {/* Profit */}
        <GlassCard testid="profit-card">
          <StatLabel icon={Wallet} tint="text-[#10B981]">Net Profit Estimator</StatLabel>
          <dl className="mt-3 space-y-1.5 text-xs sm:text-sm font-mono-num">
            <Row label="List Price" value={money(profit.list_price)} />
            <Row label="Platform Fee (10% + $0.40)" value={`-${money(profit.platform_fee)}`} negative />
            <Row label="Est. Shipping" value={`-${money(profit.shipping)}`} negative />
          </dl>
          <div className="border-t border-white/10 mt-3 pt-3 flex justify-between items-baseline">
            <span className="text-[10px] sm:text-xs uppercase tracking-widest text-white/50">Net Take-Home</span>
            <span data-testid="net-profit" className="font-mono-num font-bold text-xl sm:text-2xl text-[#10B981]">
              <CountUp value={profit.net} prefix="$" decimals={2} />
            </span>
          </div>
        </GlassCard>
      </div>
    </motion.div>
  );
}

function Row({ label, value, negative }) {
  return (
    <div className="flex justify-between">
      <span className="text-white/60">{label}</span>
      <span className={negative ? "text-white/70" : "text-white"}>{value}</span>
    </div>
  );
}
